/* Ethereal overhaul v2 (23 Oct 2026): the interactive parts.
   - Hero: slow cross-fade between photos (stops for reduced motion, when hidden, on hover or focus).
   - Shop the ear: photo dots, look picker, gold/silver switch, size per piece, live stack and save total, add all.
   - Product tabs and Find your piercing: load another collection's pieces in place (sections/eth-rail.liquid).
   - Stack builder: pick pieces, live tray with the tier reached, add the stack in one go.
   - Product page: Add to cart bar once the main button scrolls away.
   Adds go to /cart/add.js and open Dawn's cart drawer (or the cart page when the drawer is off). */
(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const money = (cents) =>
    new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(Number(cents) / 100);
  const moneyShort = (cents) => money(cents).replace(/\.00$/, '');

  const cartAddUrl = () => `${(window.routes && window.routes.cart_add_url) || '/cart/add'}.js`;
  const cartUrl = () => (window.routes && window.routes.cart_url) || '/cart';

  const postJSON = (url, body) =>
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then((response) => response.json().then((data) => ({ ok: response.ok, data })));

  /* Add items, then refresh and open the cart drawer (or go to the cart page) */
  const addToCart = (items) => {
    const drawer = document.querySelector('cart-drawer');
    const canRender = drawer && typeof drawer.renderContents === 'function';
    const sections = canRender ? drawer.getSectionsToRender().map((section) => section.id) : [];
    return postJSON(cartAddUrl(), { items, sections, sections_url: window.location.pathname }).then(({ ok, data }) => {
      if (!ok || data.status) throw new Error(data.description || data.message || '');
      if (canRender && data.sections) {
        drawer.classList.remove('is-empty');
        drawer.renderContents(data);
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'eth-v2', cartData: data });
        }
      } else {
        window.location.href = cartUrl();
      }
    });
  };

  const showError = (element, message) => {
    if (!element) return;
    element.textContent = message || 'Sorry, that could not be added. Please try again.';
    element.removeAttribute('hidden');
  };

  /* Stack and save tiers from "2:10,3:20,4:30" */
  const parseTiers = (value) =>
    (value || '')
      .split(',')
      .map((pair) => pair.split(':').map(Number))
      .filter(([qty, pct]) => qty > 0 && pct > 0)
      .map(([qty, pct]) => ({ qty, pct }))
      .sort((a, b) => a.qty - b.qty);
  const pctFor = (tiers, count) => tiers.reduce((pct, tier) => (count >= tier.qty ? Math.max(pct, tier.pct) : pct), 0);
  const discounted = (cents, pct) => cents - Math.floor((cents * pct) / 100);
  const tierMessage = (tiers, count) => {
    if (!tiers.length) return '';
    const pct = pctFor(tiers, count);
    const next = tiers.find((tier) => count < tier.qty);
    if (!next) return `Best rate: ${pct}% off every piece`;
    const need = next.qty - count;
    const more = `Add ${need} more ${need === 1 ? 'piece' : 'pieces'} for ${next.pct}% off`;
    return pct ? `${pct}% off applied. ${more}` : more;
  };

  /* Variants written by snippets/eth-variants-json.liquid */
  const readVariants = (element) => {
    const script = element.querySelector('[data-eth-variants]');
    try {
      return script ? JSON.parse(script.textContent) : [];
    } catch (error) {
      return [];
    }
  };
  const inMetal = (variants, metal) => {
    const matches = variants.filter((variant) => !variant.metal || variant.metal === metal);
    return matches.length ? matches : variants;
  };
  const pickVariant = (variants, metal, rest) => {
    const pool = inMetal(variants, metal);
    return (
      pool.find((variant) => variant.rest === rest && variant.available) ||
      pool.find((variant) => variant.available && variant.rest.indexOf('6 mm') !== -1) ||
      pool.find((variant) => variant.available) ||
      pool.find((variant) => variant.rest === rest) ||
      pool[0]
    );
  };
  const fillSizes = (select, variants, metal, chosen) => {
    if (!select) return;
    const wrapper = select.closest('label');
    const rests = [];
    inMetal(variants, metal).forEach((variant) => {
      if (variant.rest && variant.rest !== 'Default Title' && rests.indexOf(variant.rest) === -1) rests.push(variant.rest);
    });
    if (rests.length < 2) {
      if (wrapper) wrapper.hidden = true;
      return;
    }
    if (wrapper) wrapper.hidden = false;
    const key = rests.join('|');
    if (select.dataset.key !== key) {
      select.innerHTML = '';
      rests.forEach((rest) => {
        const soldOut = !inMetal(variants, metal).some((variant) => variant.rest === rest && variant.available);
        const option = new Option(soldOut ? `${rest} (sold out)` : rest, rest);
        select.add(option);
      });
      select.dataset.key = key;
    }
    select.value = chosen;
  };

  /* Hero cross-fade: the next photo (and its round close-up) fades in over the current one, which stays fully visible
     underneath until the fade ends, so nothing dips or snaps. A photo only fades in once it has loaded. */
  const initFader = (root) => {
    const slides = Array.from(root.querySelectorAll('[data-eth-slide]'));
    const details = Array.from(root.querySelectorAll('[data-eth-slide-detail]'));
    const dots = Array.from(root.querySelectorAll('[data-eth-slide-dot]'));
    if (slides.length < 2) return;
    const interval = (Number(root.dataset.interval) || 6) * 1000;
    const autoplay = root.dataset.autoplay === 'true';
    const fade = 750;
    let index = 0;
    let timer = null;
    let visible = true;
    let held = false;
    let busy = false;

    const whenLoaded = (image) =>
      new Promise((resolve) => {
        if (!image || (image.complete && image.naturalWidth > 0)) {
          resolve();
          return;
        }
        image.loading = 'eager';
        image.addEventListener('load', resolve, { once: true });
        image.addEventListener('error', resolve, { once: true });
      });

    const show = (next) => {
      next = (next + slides.length) % slides.length;
      if (next === index || busy) return;
      busy = true;
      whenLoaded(slides[next].querySelector('img')).then(() => {
        const previous = index;
        [slides, details].forEach((list) => {
          if (!list[next] || !list[previous]) return;
          list.forEach((item) => item.classList.remove('is-leaving'));
          list[previous].classList.remove('is-active');
          list[previous].classList.add('is-leaving');
          list[next].classList.add('is-active');
        });
        index = next;
        dots.forEach((dot, i) => {
          dot.classList.toggle('is-active', i === index);
          if (i === index) dot.setAttribute('aria-current', 'true');
          else dot.removeAttribute('aria-current');
        });
        setTimeout(
          () => {
            [slides, details].forEach((list) => list[previous] && list[previous].classList.remove('is-leaving'));
            busy = false;
          },
          reduceMotion.matches ? 0 : fade
        );
      });
    };

    /* Load the other photos once the page itself has finished loading */
    const warm = () =>
      slides.forEach((slide) => {
        const image = slide.querySelector('img');
        if (image) image.loading = 'eager';
      });
    if (document.readyState === 'complete') setTimeout(warm, 1200);
    else window.addEventListener('load', () => setTimeout(warm, 1200), { once: true });
    const stop = () => {
      clearInterval(timer);
      timer = null;
    };
    const start = () => {
      stop();
      if (!autoplay || reduceMotion.matches || !visible || held || document.hidden) return;
      timer = setInterval(() => show(index + 1), interval);
    };

    dots.forEach((dot, i) =>
      dot.addEventListener('click', () => {
        show(i);
        start();
      })
    );
    root.addEventListener('mouseenter', () => {
      held = true;
      stop();
    });
    root.addEventListener('mouseleave', () => {
      held = false;
      start();
    });
    root.addEventListener('focusin', () => {
      held = true;
      stop();
    });
    root.addEventListener('focusout', () => {
      held = false;
      start();
    });
    document.addEventListener('visibilitychange', start);
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(([entry]) => {
        visible = entry.isIntersecting;
        start();
      }).observe(root);
    }
    start();
  };

  /* Scrolling band: a tap pauses it on touch screens (hover and focus pause it in CSS) */
  const initMarquee = (root) => {
    root.addEventListener('click', (event) => {
      if (event.target.closest('a')) return;
      root.classList.toggle('is-paused');
    });
  };

  /* Shop the ear */
  const initLook = (look) => {
    const panel = look.querySelector('[data-eth-look-panel]');
    if (!panel) return;
    const tiers = parseTiers(panel.dataset.tiers);
    const addButton = panel.querySelector('[data-eth-look-add]');
    const addLabel = panel.querySelector('[data-eth-look-add-label]');
    const error = panel.querySelector('[data-eth-look-error]');
    const tierText = panel.querySelector('[data-eth-look-tier]');
    const was = panel.querySelector('[data-eth-look-was]');
    const now = panel.querySelector('[data-eth-look-now]');
    const hotspots = Array.from(look.querySelectorAll('[data-eth-hotspot]'));
    let metal = '18K Gold Vermeil';

    const rows = Array.from(panel.querySelectorAll('[data-eth-look-row]')).map((element) => ({
      element,
      index: element.dataset.ethLookRow,
      variants: readVariants(element),
      check: element.querySelector('[data-eth-row-check]'),
      size: element.querySelector('[data-eth-row-size]'),
      price: element.querySelector('[data-eth-row-price]'),
      image: element.querySelector('[data-eth-row-image]'),
      rest: null,
      skipped: false,
      variant: null,
    }));
    rows.forEach((row) => {
      if (row.image) row.image.dataset.fallback = row.image.src;
    });

    const render = () => {
      let count = 0;
      let total = 0;
      let after = 0;
      const pickedRows = [];
      rows.forEach((row) => {
        row.variant = pickVariant(row.variants, metal, row.rest);
        if (!row.variant) return;
        row.rest = row.variant.rest;
        fillSizes(row.size, row.variants, metal, row.rest);
        const available = Boolean(row.variant.available);
        row.check.disabled = !available;
        row.check.checked = available && !row.skipped;
        row.element.classList.toggle('is-soldout', !available);
        row.element.classList.toggle('is-skipped', available && row.skipped);
        row.price.textContent = available ? money(row.variant.price) : 'Sold out';
        if (row.image) row.image.src = row.variant.image || row.image.dataset.fallback;
        const hotspot = hotspots.find((spot) => spot.dataset.ethHotspot === row.index);
        const tipPrice = hotspot && hotspot.querySelector('[data-eth-tip-price]');
        if (tipPrice) tipPrice.textContent = available ? moneyShort(row.variant.price) : 'Sold out';
        if (row.check.checked) pickedRows.push(row);
      });
      count = pickedRows.length;
      const pct = pctFor(tiers, count);
      pickedRows.forEach((row) => {
        total += row.variant.price;
        after += discounted(row.variant.price, pct);
      });
      if (tierText) {
        tierText.textContent = !count
          ? 'Tick the pieces you want'
          : pct
          ? `${count} pieces, stack and save ${pct}% off`
          : tierMessage(tiers, count);
      }
      if (was) was.textContent = pct ? money(total) : '';
      if (now) now.textContent = count ? money(after) : '';
      if (addButton) addButton.disabled = count === 0;
      if (addLabel) {
        addLabel.textContent = count
          ? `Add ${count} ${count === 1 ? 'piece' : 'pieces'} to cart`
          : 'Choose a piece';
      }
    };

    panel.addEventListener('change', (event) => {
      const target = event.target;
      if (target.matches('[data-eth-look-metal]')) {
        metal = target.value;
        rows.forEach((row) => {
          row.rest = null;
        });
      }
      const row = rows.find((item) => item.element.contains(target));
      if (row && target === row.check) row.skipped = !target.checked;
      if (row && target === row.size) row.rest = target.value;
      render();
    });

    const closeTips = (except) =>
      hotspots.forEach((spot) => {
        if (spot !== except) spot.classList.remove('is-open');
      });
    hotspots.forEach((spot) => {
      spot.addEventListener('click', () => {
        const open = !spot.classList.contains('is-open');
        closeTips(spot);
        spot.classList.toggle('is-open', open);
        const row = rows.find((item) => item.index === spot.dataset.ethHotspot);
        rows.forEach((item) => item.element.classList.toggle('is-highlight', open && item === row));
      });
      spot.addEventListener('mouseenter', () => {
        const row = rows.find((item) => item.index === spot.dataset.ethHotspot);
        if (row) row.element.classList.add('is-highlight');
      });
      spot.addEventListener('mouseleave', () => {
        if (!spot.classList.contains('is-open')) rows.forEach((item) => item.element.classList.remove('is-highlight'));
      });
    });
    rows.forEach((row) => {
      const spot = hotspots.find((item) => item.dataset.ethHotspot === row.index);
      if (!spot) return;
      row.element.addEventListener('mouseenter', () => spot.classList.add('is-lit'));
      row.element.addEventListener('mouseleave', () => spot.classList.remove('is-lit'));
    });
    document.addEventListener('click', (event) => {
      if (!look.contains(event.target)) closeTips(null);
    });

    if (addButton) {
      addButton.addEventListener('click', () => {
        const items = rows
          .filter((row) => row.check.checked && row.variant)
          .map((row) => ({ id: row.variant.id, quantity: 1 }));
        if (!items.length) return;
        addButton.setAttribute('aria-busy', 'true');
        if (error) error.setAttribute('hidden', '');
        addToCart(items)
          .catch((problem) => showError(error, problem.message))
          .finally(() => addButton.removeAttribute('aria-busy'));
      });
    }

    render();
  };

  const initLooks = (root) => {
    const tabs = Array.from(root.querySelectorAll('[data-eth-look-tab]'));
    tabs.forEach((tab) =>
      tab.addEventListener('click', () => {
        tabs.forEach((other) => {
          const selected = other === tab;
          other.setAttribute('aria-selected', String(selected));
          const panel = document.getElementById(other.getAttribute('aria-controls'));
          if (panel) panel.hidden = !selected;
        });
      })
    );
    root.querySelectorAll('[data-eth-look]').forEach(initLook);
  };

  /* Product rows loaded from another collection */
  const railCache = new Map();
  const loadRail = (url, panel) => {
    if (!url || !panel) return;
    panel.dataset.ethWant = url;
    if (railCache.has(url)) {
      panel.innerHTML = railCache.get(url);
      return;
    }
    panel.classList.add('is-loading');
    panel.setAttribute('aria-busy', 'true');
    fetch(`${url}${url.indexOf('?') === -1 ? '?' : '&'}section_id=eth-rail`)
      .then((response) => response.text())
      .then((html) => {
        const source = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-eth-rail-source]');
        const inner = source ? source.innerHTML : '';
        railCache.set(url, inner);
        if (panel.dataset.ethWant === url) panel.innerHTML = inner;
      })
      .catch(() => {
        window.location.href = url;
      })
      .finally(() => {
        panel.classList.remove('is-loading');
        panel.removeAttribute('aria-busy');
      });
  };

  const initTabs = (root) => {
    const panel = root.querySelector('[data-eth-tab-panel]');
    const chips = Array.from(root.querySelectorAll('[data-eth-tab-url]'));
    if (!panel || !chips.length) return;
    railCache.set(chips[0].dataset.ethTabUrl, panel.innerHTML);
    chips.forEach((chip) =>
      chip.addEventListener('click', () => {
        if (chip.getAttribute('aria-selected') === 'true') return;
        chips.forEach((other) => other.setAttribute('aria-selected', String(other === chip)));
        loadRail(chip.dataset.ethTabUrl, panel);
      })
    );
  };

  /* Find your piercing */
  const initMap = (root) => {
    const section = root.closest('.eth-map') || root;
    const panel = section.querySelector('[data-eth-map-panel]');
    const chips = Array.from(root.querySelectorAll('.eth-chip[data-eth-map-pick]'));
    const dots = Array.from(root.querySelectorAll('.eth-map__dot'));
    const infos = Array.from(root.querySelectorAll('[data-eth-map-info]'));
    if (!chips.length) return;
    if (chips[0].dataset.ethMapUrl && panel) railCache.set(chips[0].dataset.ethMapUrl, panel.innerHTML);

    const pick = (index) => {
      const chip = chips[index];
      if (!chip) return;
      chips.forEach((other) => other.setAttribute('aria-selected', String(other === chip)));
      dots.forEach((dot) => dot.classList.toggle('is-active', dot.dataset.ethMapPick === String(index)));
      infos.forEach((info) => {
        info.hidden = info.dataset.ethMapInfo !== String(index);
      });
      if (chip.dataset.ethMapUrl) loadRail(chip.dataset.ethMapUrl, panel);
    };
    root.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-eth-map-pick]');
      if (!trigger || !root.contains(trigger)) return;
      pick(Number(trigger.dataset.ethMapPick));
    });
  };

  /* Stack builder */
  const initBuilder = (root) => {
    const tiers = parseTiers(root.dataset.tiers);
    const topQty = tiers.length ? tiers[tiers.length - 1].qty : 4;
    const tray = root.querySelector('[data-eth-tray]');
    const trayPieces = root.querySelector('[data-eth-tray-pieces]');
    const trayCount = root.querySelector('[data-eth-tray-count]');
    const trayMessage = root.querySelector('[data-eth-tray-message]');
    const trayFill = root.querySelector('[data-eth-tray-fill]');
    const trayWas = root.querySelector('[data-eth-tray-was]');
    const trayNow = root.querySelector('[data-eth-tray-now]');
    const trayAdd = root.querySelector('[data-eth-tray-add]');
    const trayError = root.querySelector('[data-eth-tray-error]');
    const empty = root.querySelector('[data-eth-builder-empty]');
    let metal = '18K Gold Vermeil';
    let filter = 'all';
    const picked = [];

    const cards = Array.from(root.querySelectorAll('[data-eth-bcard]')).map((element) => ({
      element,
      title: element.dataset.title,
      cat: element.dataset.cat,
      variants: readVariants(element),
      size: element.querySelector('[data-eth-bcard-size]'),
      price: element.querySelector('[data-eth-bcard-price]'),
      image: element.querySelector('[data-eth-bcard-image]'),
      button: element.querySelector('[data-eth-bcard-add]'),
      rest: null,
      variant: null,
    }));
    cards.forEach((card) => {
      if (card.image) card.image.dataset.fallback = card.image.src;
    });

    const renderTray = () => {
      const count = picked.length;
      const pct = pctFor(tiers, count);
      let total = 0;
      let after = 0;
      picked.forEach((card) => {
        total += card.variant.price;
        after += discounted(card.variant.price, pct);
      });
      trayPieces.innerHTML = '';
      picked.forEach((card) => {
        const item = document.createElement('li');
        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'eth-tray__piece';
        remove.setAttribute('aria-label', `Remove ${card.title}`);
        const thumb = document.createElement('img');
        thumb.src = (card.image && card.image.src) || '';
        thumb.alt = '';
        thumb.width = 44;
        thumb.height = 44;
        remove.appendChild(thumb);
        remove.addEventListener('click', () => toggle(card));
        item.appendChild(remove);
        trayPieces.appendChild(item);
      });
      trayCount.textContent = count
        ? `${count} ${count === 1 ? 'piece' : 'pieces'} in your stack`
        : 'Your stack is empty';
      if (trayMessage) trayMessage.textContent = tierMessage(tiers, count);
      if (trayFill) trayFill.style.width = `${Math.min(count / topQty, 1) * 100}%`;
      trayWas.textContent = pct ? money(total) : '';
      trayNow.textContent = count ? money(after) : '';
      trayAdd.disabled = count === 0;
      trayAdd.textContent = count ? `Add ${count} ${count === 1 ? 'piece' : 'pieces'} to cart` : 'Add stack to cart';
      tray.classList.toggle('has-pieces', count > 0);
    };

    const renderCard = (card) => {
      card.variant = pickVariant(card.variants, metal, card.rest);
      if (!card.variant) return;
      card.rest = card.variant.rest;
      fillSizes(card.size, card.variants, metal, card.rest);
      const available = Boolean(card.variant.available);
      card.price.textContent = available ? money(card.variant.price) : 'Sold out';
      if (card.image) card.image.src = card.variant.image || card.image.dataset.fallback;
      const inStack = picked.indexOf(card) !== -1;
      if (!available && inStack) picked.splice(picked.indexOf(card), 1);
      const isPicked = available && inStack;
      card.element.classList.toggle('is-picked', isPicked);
      card.element.classList.toggle('is-soldout', !available);
      card.button.disabled = !available;
      card.button.setAttribute('aria-pressed', String(isPicked));
      card.button.textContent = !available ? 'Sold out' : isPicked ? 'In your stack' : 'Add to stack';
    };

    const applyFilter = () => {
      let visibleCount = 0;
      cards.forEach((card) => {
        const show = filter === 'all' || card.cat === filter;
        card.element.hidden = !show;
        if (show) visibleCount += 1;
      });
      if (empty) empty.hidden = visibleCount > 0;
    };

    const toggle = (card) => {
      const at = picked.indexOf(card);
      if (at === -1) {
        if (!card.variant || !card.variant.available) return;
        picked.push(card);
      } else {
        picked.splice(at, 1);
      }
      renderCard(card);
      renderTray();
    };

    root.addEventListener('click', (event) => {
      const add = event.target.closest('[data-eth-bcard-add]');
      if (add) {
        const card = cards.find((item) => item.button === add);
        if (card) toggle(card);
        return;
      }
      const chip = event.target.closest('[data-eth-filter]');
      if (chip) {
        filter = chip.dataset.ethFilter;
        root.querySelectorAll('[data-eth-filter]').forEach((other) => other.setAttribute('aria-pressed', String(other === chip)));
        applyFilter();
      }
    });
    root.addEventListener('change', (event) => {
      if (event.target.matches('[data-eth-builder-metal]')) {
        metal = event.target.value;
        cards.forEach((card) => {
          card.rest = null;
          renderCard(card);
        });
        renderTray();
        return;
      }
      const card = cards.find((item) => item.size === event.target);
      if (card) {
        card.rest = event.target.value;
        renderCard(card);
        renderTray();
      }
    });

    trayAdd.addEventListener('click', () => {
      const items = picked.map((card) => ({ id: card.variant.id, quantity: 1 }));
      if (!items.length) return;
      trayAdd.setAttribute('aria-busy', 'true');
      if (trayError) trayError.setAttribute('hidden', '');
      addToCart(items)
        .catch((problem) => showError(trayError, problem.message))
        .finally(() => trayAdd.removeAttribute('aria-busy'));
    });

    cards.forEach(renderCard);
    applyFilter();
    renderTray();
  };

  /* Product page: Add to cart bar */
  const initSticky = (bar) => {
    const sectionId = bar.dataset.sectionId;
    const mainButton = document.getElementById(`ProductSubmitButton-${sectionId}`);
    const button = bar.querySelector('[data-eth-sticky-button]');
    const price = bar.querySelector('[data-eth-sticky-price]');
    const variantText = bar.querySelector('[data-eth-sticky-variant]');
    if (!mainButton || !button || !('IntersectionObserver' in window)) return;
    bar.hidden = false;

    const sync = () => {
      const label = mainButton.querySelector('span');
      button.textContent = (label ? label.textContent : mainButton.textContent).trim();
      button.disabled = mainButton.hasAttribute('disabled');
      const priceBox = document.getElementById(`price-${sectionId}`);
      if (priceBox && price) {
        const onSale = priceBox.querySelector('.price--on-sale .price__sale .price-item--sale');
        const regular = priceBox.querySelector('.price__regular .price-item--regular');
        const source = onSale || regular;
        if (source) price.textContent = source.textContent.trim();
      }
    };
    new MutationObserver(sync).observe(mainButton, { attributes: true, childList: true, subtree: true, characterData: true });
    const priceBox = document.getElementById(`price-${sectionId}`);
    if (priceBox) new MutationObserver(sync).observe(priceBox, { childList: true, subtree: true, characterData: true });
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        const variant = event && event.data && event.data.variant;
        if (variant && variantText && event.data.sectionId === sectionId) variantText.textContent = variant.title;
        setTimeout(sync, 50);
      });
    }

    new IntersectionObserver(([entry]) => {
      const past = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      bar.classList.toggle('is-visible', past);
      document.body.classList.toggle('eth-has-sticky', past);
    }).observe(mainButton);

    button.addEventListener('click', () => mainButton.click());
    sync();
  };

  /* Cart drawer on phones: "Have a discount code?" opens the discount app's code field in the pinned footer.
     The drawer re-renders on every cart change, so the field starts closed again after an update. */
  document.addEventListener('click', (event) => {
    const toggle = event.target.closest && event.target.closest('[data-eth-code-toggle]');
    if (!toggle) return;
    const footer = toggle.closest('.drawer__footer');
    if (!footer) return;
    footer.classList.add('eth-show-code');
    toggle.setAttribute('aria-expanded', 'true');
    const input = footer.querySelector('.sc_simple-container input:not([type="submit"]):not([type="hidden"])');
    if (input) input.focus();
  });

  /* Collection chips: bring the current one into view on phones */
  const initColnav = (root) => {
    const current = root.querySelector('[aria-current="page"]');
    const list = root.querySelector('.eth-colnav__list');
    if (current && list && list.scrollWidth > list.clientWidth) {
      list.scrollLeft = current.offsetLeft - (list.clientWidth - current.offsetWidth) / 2;
    }
  };

  const init = (scope = document) => {
    scope.querySelectorAll('[data-eth-fader]').forEach(initFader);
    scope.querySelectorAll('[data-eth-marquee]').forEach(initMarquee);
    scope.querySelectorAll('[data-eth-looks]').forEach(initLooks);
    scope.querySelectorAll('[data-eth-tabs]').forEach(initTabs);
    scope.querySelectorAll('[data-eth-map]').forEach(initMap);
    scope.querySelectorAll('[data-eth-builder]').forEach(initBuilder);
    scope.querySelectorAll('[data-eth-sticky]').forEach(initSticky);
    scope.querySelectorAll('.eth-colnav').forEach(initColnav);
  };

  document.addEventListener('shopify:section:load', (event) => init(event.target));

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }
})();
